import * as md5 from 'md5';
import { v4 as uuidV4 } from 'uuid';
import * as browserStorage from 'data/browserStorage';
import { COOKIE_STORAGE } from '../../data/browserStorage/constants';

import { UserRoles as bronteRoles } from '../../constants/enums';
import GTMResolver from './GTMResolver';

export const SESSION_ID = 'sessionId';

export default (() => {
  let gtmResolver;
  let isRegistered = false;
  /** Do we send Anonymous user information to GTM or has already been sent.
   *  Note every time we load workspace even if the user is login
   *  it will mount login page code and trigger GTM event
   */
  let sendAnonymousUserInformation = true;
  // Caching the userParams here
  let _userParams;

  const _userParamsEmpty = {
    personId: null,
    personIdType: null,
    personRoleCode: null,
    sessionId: null,
    organizationId: null,
  };

  /**
   * Method to push events to GTM dataLayer with user info
   * @param data - the event object that will be pushed to dataLayer
   */
  const _pushGTMEvent = (data = {}) => {
    try {
      if (data !== {}) {
        window.dataLayer.push(data);
      }
    } catch (e) {
      // do nothing
    }
  };

  /**
   * Method to push events to GTM dataLayer with user Info
   * @param data - the event object that will be pushed to dataLayer
   */
  const _pushGTMEventWithUserInfo = async (data) => {
    if (_userParams) {
      _pushGTMEvent(data);
    } else {
      /**
       * We only want to send anonymous user once per session but there more then one
       * call to this tracking function
       * */ 
      if(sendAnonymousUserInformation) {
        _pushGTMEvent(_userParamsEmpty);
        sendAnonymousUserInformation = false;
      }

      _pushGTMEvent(data);
    }
  };

  /**
   * Method to set user params
   * @param currentUser - Object containing account info
   */
  const setUserParams = (currentUser) => {
    const { bearerToken } = gtmResolver.getBearerInfo();
    const sessionId = getSessionIdFromBearerToken(bearerToken);
    _userParams = {
      personId: currentUser.accountId || null,
      personIdType: 'BRONTE-INTERNAL', // TODO: Change to PI/IES
      personRoleCode: getPearsonRoleCode(currentUser.roles),
      sessionId: sessionId || null,
      organizationId: currentUser.subscriptionId || null,
    };
    _pushGTMEventWithUserInfo(_userParams);

    const sessionIdFromCookie = browserStorage.load(SESSION_ID, COOKIE_STORAGE);
    if (!sessionIdFromCookie || sessionIdFromCookie !== sessionId) {
      browserStorage.save(SESSION_ID, sessionId, COOKIE_STORAGE);
      pushUserSessionEnvironmentEvent();
    }
  };

  /**
   * Method to push browsers global parameters
   * @param browserGlobalParams - clients browser information
   */
  const pushGlobalParams = (browserGlobalParams) => {
    _pushGTMEvent(browserGlobalParams);
  };

  /**
   * Bronte roles to GTM roles
   * @param roles - One of INSTRUCTOR, AERO_INSTRUCTOR, STUDENT, STUDENT_GUEST, DEVELOPER, ADMIN, SUPPORT
   * @returns {string} - One of ["Student", "Instructor", "TeachingAssistant", "Parent", "Administrator","Employee", "Other"]
   */
  const getPearsonRoleCode = (roles = []) => {
    if (roles.includes(bronteRoles.SUPPORT)) {
      return 'Employee'; // Make consts in file
    }
    if (roles.includes(bronteRoles.ADMIN)) {
      return 'Administrator';
    }
    if (roles.includes(bronteRoles.DEVELOPER)) {
      return 'TeachingAssistant';
    }
    if (roles.includes(bronteRoles.AERO_INSTRUCTOR)) {
      return 'Instructor';
    }
    if (roles.includes(bronteRoles.STUDENT || roles.includes(bronteRoles.STUDENT_GUEST))) {
      return 'STUDENT';
    }
    return 'OTHER';
  };

  const getSessionIdFromBearerToken = (bearerToken) => md5(bearerToken);

  /**
   * Method to push user session environment event on successful authentication
   */
  const pushUserSessionEnvironmentEvent = () => {
    const userSessionEnv = {
      event: 'usersessionenvironment',
      transactionDt: new Date().toISOString(),
      messageId: uuidV4(),
    };
    _pushGTMEventWithUserInfo(userSessionEnv);
  };

  /**
   * Method to get current local time
   * @returns {Promise<string>} - Current local timestamp
   */
  const getCurrentLocalTime = () => new Date(`${new Date().toString().split('GMT')[0]} UTC`)
    .toISOString()
    .replace('Z', '');

  /**
   * Method to push each & every page rendering after the user’s authentication.
   * @param pageObj
   * @param pageErrors - list of page error where PageError is {errorCode: '',errorUrl: '', errorMessage: '', errorStateCode: '', errorDetail: ''}
   */
  const pushPageViewEvent = (pageObj, pageErrors = []) => {
    try {
      const { pageName = null, courseId = null, courseSectionId = null, contentId = null, contentType = null } = pageObj;
      const pageViewEventObj = {
        event: 'pageview',
        pageName, // Page Name -> Interactive Name
        productId: 'NA',
        productPlatformCode: 'BRONTE', // Product Platform, Ex: GLP, ILP, MASTERING
        productModelName: 'BRONTE', // Name of Product, Ex: REVEL, ETEXT2_PXE, ETEXT2_CITE
        courseId, // Course ID -> Cohort ID
        courseSectionId, // Course Section ID -> Pathway ID
        contentId, // Content ID -> Interactive ID
        contentType, // Type of the Content -> INTERACTIVE
        messageId: uuidV4(), // UUID
        transactionDt: new Date().toISOString(), // Timestamp in ISO Format: 2020-04-15T12:33:45.237Z
        transactionLocalDt: getCurrentLocalTime(),
        pageErrors,
      };
      _pushGTMEventWithUserInfo(pageViewEventObj);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log('Failed to push Page event');
    }
  };

  /**
   * Method to push user telemetry page activity event, once per page
   */
  const pushUserTelemetryPageActivity = () => {
    const initTelemetryEvent = {
      event: 'inittelemetry',
    };
    _pushGTMEventWithUserInfo(initTelemetryEvent);
  };

  /**
   * Method to push both pushUserTelemetryPageActivity & pushPageViewEvent.
   * @param pageObj - aspects of a page
   * @param pageErrors - Errors object if there is a page error
   */
  const pushTelemetryPageActivityAndView = (pageObj, pageErrors) => {
    pushUserTelemetryPageActivity();
    pushPageViewEvent(pageObj, pageErrors);
  };

  /**
   * Method to push users logout event
   */
  const pushUserLogoutEvent = () => {
    const logoutEvent = {
      event: 'logout',
      messageId: uuidV4(), // UUID v4
      transactionDt: new Date().toISOString(), // Timestamp in ISO Format: 2020-04-15T12:33:45.237Z
    };
    _pushGTMEventWithUserInfo(logoutEvent);
    // Clear cached values on logout
    _userParams = undefined;

    /**
        * Setting sendAnonymousUserInformation to true so next GTM push
        * we send the user as Anonymous user for first push to GTM
      */ 

    sendAnonymousUserInformation = true;

  };

  /**
   * Method to push page action
   * @param pageAction
   */
  const pushTelemetryPageAction = (pageAction) => {
    const { eventCategory = null, eventAction = null, eventLabel = null, eventValue = null } = pageAction;
    const telemetryPageActionEventObj = {
      event: 'telemetrypageaction',
      eventCategory,
      eventAction,
      eventLabel,
      eventValue,
      transactionDt: new Date().toISOString(),
    };
    _pushGTMEventWithUserInfo(telemetryPageActionEventObj);
  };

  /**
   * Method to push page errors
   * @param pageError
   */
  const pushTelemetryPageError = (pageError) => {
    const { errorCode = null, errorUrl = null, errorMessage = null, errorStateCode = null, errorDetail = null } = pageError;
    const telemetryPageActionEventObj = {
      event: 'telemetrypageerror',
      errorCode,
      errorUrl,
      errorMessage,
      errorStateCode,
      errorDetail,
      transactionDt: new Date().toISOString(),
    };
    _pushGTMEventWithUserInfo(telemetryPageActionEventObj);
  };

  return {
    isRegistered: () => isRegistered,
    register: (connection) => {
      isRegistered = true;
      gtmResolver = new GTMResolver(connection);
    },
    setUserParams,
    pushGlobalParams,
    pushUserSessionEnvironmentEvent,
    pushPageViewEvent,
    pushUserTelemetryPageActivity,
    pushUserLogoutEvent,
    pushTelemetryPageActivityAndView,
    pushTelemetryPageAction,
    pushTelemetryPageError,
  };
})();
